/* Native dialog + canvas with Klaviyo public client endpoints. No private credentials. */
(() => {
  if (customElements.get('ocu-scratch-signup')) return;
  class OcuScratchSignup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.card = this.querySelector('[data-scratch]');
      this.canvas = this.querySelector('canvas');
      this.form = this.querySelector('[data-email-form]');
      this.preview = this.dataset.preview === 'true';
      this.storageKey = 'ocu-scratch-signup-v1';
      this.email = '';
      this.busy = false;
      this.state = 'scratch';
      this.emailCompleted = false;
      this.revealed = false;
      this.dragging = false;
      this.cells = new Set();
      const options = { signal: this.controller.signal };
      this.querySelector('[data-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.querySelector('[data-claim]').addEventListener('click', () => {
        if (!this.revealed) { this.reveal(true); return; }
        this.showStep('email');
      }, options);
      for (const choice of this.querySelectorAll('[data-product-choice]')) {
        choice.addEventListener('click', () => this.chooseProduct(choice), options);
      }
      this.card.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.reveal(true); }
      }, options);
      this.card.addEventListener('pointerdown', event => this.pointerDown(event), options);
      this.card.addEventListener('pointermove', event => this.pointerMove(event), options);
      this.card.addEventListener('pointerup', event => this.pointerUp(event), options);
      this.card.addEventListener('pointercancel', () => { this.dragging = false; }, options);
      // No native Shopify form: consent goes directly to the dedicated Klaviyo list.
      this.querySelector('[data-submit]').addEventListener('click', event => this.submit(event), options);
      this.form.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); this.submit(event); }
      }, options);
      this.querySelector('[data-copy]')?.addEventListener('click', async event => {
        const button = event.currentTarget;
        try {
          await navigator.clipboard.writeText(this.dataset.code);
          button.textContent = 'Copied!';
        } catch {
          this.querySelector('[data-status]').textContent = 'Select the code above to copy it.';
        }
      }, options);
      this.observer = new ResizeObserver(() => { if (this.dialog.open && !this.revealed) this.drawCover(); });
      this.observer.observe(this.card);
      this.delayMs = this.preview ? 500 : Number(this.dataset.delay || 25000);
      this.viewStorageKey = 'ocu-signup-view-ms-v1';
      this.viewedMs = this.readViewingTime();
      this.viewStarted = null;
      const remembered = this.readMemory();
      if (!this.preview && (this.dataset.subscriber === 'true' || remembered)) return;
      this.waitingToOpen = true;
      document.addEventListener('visibilitychange', () => this.scheduleOpen(), options);
      window.addEventListener('pagehide', () => this.pauseTimer(), options);
      window.addEventListener('pageshow', () => this.scheduleOpen(), options);
      this.scheduleOpen();
    }
    disconnectedCallback() {
      this.pauseTimer();
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.dialog?.open) { this.dialog.close(); this.restoreScroll(); }
      this.initialized = false;
    }
    readViewingTime() {
      if (this.preview) return 0;
      try {
        const value = Number(sessionStorage.getItem(this.viewStorageKey));
        return Number.isFinite(value) ? Math.max(0, Math.min(value, this.delayMs)) : 0;
      } catch { return 0; }
    }
    pauseTimer() {
      clearTimeout(this.timer);
      if (this.viewStarted == null) return;
      this.viewedMs = Math.min(this.delayMs, this.viewedMs + Math.max(0, performance.now() - this.viewStarted));
      this.viewStarted = null;
      if (!this.preview) {
        try { sessionStorage.setItem(this.viewStorageKey, String(this.viewedMs)); } catch { /* Timing still works on this page without storage. */ }
      }
    }
    scheduleOpen() {
      this.pauseTimer();
      if (!this.waitingToOpen || !this.isConnected || this.dialog.open || document.visibilityState !== 'visible') return;
      if (!this.preview && this.readMemory()) { this.waitingToOpen = false; return; }
      this.viewStarted = performance.now();
      this.timer = window.setTimeout(() => { this.pauseTimer(); this.autoOpen(); }, Math.max(0, this.delayMs - this.viewedMs));
    }
    readMemory() {
      try {
        const value = JSON.parse(localStorage.getItem(this.storageKey));
        return value && value.until > Date.now() ? value : null;
      } catch { return null; }
    }
    remember(state) {
      if (this.preview) return;
      const days = state === 'subscribed' ? 365 : Number(this.dataset.dismissDays || 7);
      try { localStorage.setItem(this.storageKey, JSON.stringify({ state, until: Date.now() + days * 86400000 })); } catch { /* Storage restrictions do not block signup. */ }
    }
    autoOpen() {
      if (!this.waitingToOpen || !this.isConnected || this.dialog.open) return;
      if (!this.preview && this.readMemory()) { this.waitingToOpen = false; return; }
      if (!this.preview && (document.visibilityState !== 'visible' || this.hasOpenModal())) {
        this.timer = window.setTimeout(() => this.autoOpen(), 3000);
        return;
      }
      this.open();
    }
    hasOpenModal() {
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(modal => {
        const rect = modal.getBoundingClientRect();
        return getComputedStyle(modal).visibility !== 'hidden'
          && !modal.closest('[hidden], [aria-hidden="true"]')
          && rect.width > 0 && rect.height > 0
          && rect.top < window.innerHeight && rect.bottom > 0
          && rect.left < window.innerWidth && rect.right > 0;
      });
    }
    open() {
      if (this.dialog.open) return;
      this.waitingToOpen = false;
      this.pauseTimer();
      this.returnFocus = document.activeElement;
      this.oldOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      if (!this.revealed) this.drawCover();
      this.focusStep();
      this.emit('opened');
    }
    restoreScroll() { document.documentElement.style.overflow = this.oldOverflow || ''; }
    close() {
      this.dialog.close();
      this.restoreScroll();
      this.remember(this.emailCompleted ? 'subscribed' : 'dismissed');
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.emit('closed');
    }
    showStep(step, focus = true) {
      this.state = step;
      this.dataset.stage = step;
      this.querySelector('[data-status]').textContent = '';
      for (const panel of this.querySelectorAll('[data-step]')) {
        panel.hidden = panel.dataset.step !== step;
        panel.classList.toggle('ocu-signup__step--enter', panel.dataset.step === step);
      }
      this.dialog.scrollTop = 0;
      if (focus && this.dialog.open) this.focusStep();
      this.emit(step);
    }
    focusStep() {
      if (this.state === 'email') this.form.querySelector('input[type="email"]').focus();
      else if (this.state === 'preferences') this.querySelector('[data-preferences-title]').focus({ preventScroll: true });
      else if (this.state === 'success') this.querySelector('[data-success-title]').focus({ preventScroll: true });
      else {
        this.dialog.tabIndex = -1;
        this.dialog.focus({ preventScroll: true });
      }
    }
    drawCover() {
      const size = this.card.getBoundingClientRect().width;
      if (!size) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(size * ratio);
      this.canvas.height = Math.round(size * ratio);
      this.ctx = this.canvas.getContext('2d');
      const ctx = this.ctx;
      ctx.scale(ratio, ratio);
      this.cells.clear();
      this.card.dataset.scratching = 'false';
      const x = size * .5, y = size * .475, radius = size * .333;
      ctx.shadowColor = '#1c304c44'; ctx.shadowBlur = 7; ctx.shadowOffsetY = 3;
      const ring = ctx.createLinearGradient(0, y-radius, 0, y+radius);
      ring.addColorStop(0, '#ffffff'); ring.addColorStop(.6, '#e9e9e8'); ring.addColorStop(1, '#a9afb3');
      ctx.fillStyle = ring; ctx.beginPath(); ctx.arc(x,y,radius,0,Math.PI*2); ctx.fill();
      ctx.shadowColor = 'transparent';
      for (let i = 0; i < 170; i++) {
        const angle = i / 170 * Math.PI * 2;
        ctx.strokeStyle = i % 2 ? '#b9bec1' : '#ffffff'; ctx.lineWidth = .75;
        ctx.beginPath(); ctx.moveTo(x+Math.cos(angle)*radius*.91,y+Math.sin(angle)*radius*.91);
        ctx.lineTo(x+Math.cos(angle)*radius*.982,y+Math.sin(angle)*radius*.982); ctx.stroke();
      }
      const face = ctx.createRadialGradient(x-radius*.25,y-radius*.3,0,x,y,radius);
      face.addColorStop(0,'#ffffff'); face.addColorStop(.76,'#f3f3f1'); face.addColorStop(1,'#d5d8d8');
      ctx.fillStyle = face; ctx.beginPath(); ctx.arc(x,y,radius*.9,0,Math.PI*2); ctx.fill();
      ctx.strokeStyle = '#bfc5c7'; ctx.lineWidth = 1; ctx.stroke();
    }
    point(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top, width: rect.width };
    }
    pointerDown(event) {
      if (this.revealed || event.button !== 0 || !this.ctx) return;
      event.preventDefault(); this.dragging = true; this.distance = 0;
      this.last = this.point(event); this.card.setPointerCapture(event.pointerId);
      this.card.dataset.scratching = 'true'; this.erase(this.last, this.last);
    }
    pointerMove(event) {
      if (!this.dragging || this.revealed) return;
      const next = this.point(event);
      this.distance += Math.hypot(next.x-this.last.x,next.y-this.last.y);
      this.erase(this.last,next); this.last = next;
      if (this.cells.size >= 105) this.reveal();
    }
    pointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.card.hasPointerCapture(event.pointerId)) this.card.releasePointerCapture(event.pointerId);
      if (this.distance < 12) this.reveal();
    }
    erase(from,to) {
      const ctx = this.ctx, width = to.width, brush = width*.09;
      ctx.globalCompositeOperation = 'destination-out'; ctx.lineWidth = brush*2; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(from.x,from.y); ctx.lineTo(to.x,to.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(to.x,to.y,brush,0,Math.PI*2); ctx.fill();
      const steps = Math.max(1,Math.ceil(Math.hypot(to.x-from.x,to.y-from.y)/(width/30)));
      for (let step=0; step<=steps; step++) {
        const px=from.x+(to.x-from.x)*step/steps, py=from.y+(to.y-from.y)*step/steps;
        for (let gx=0;gx<24;gx++) for (let gy=0;gy<24;gy++) {
          const cx=(gx+.5)*width/24, cy=(gy+.5)*width/24;
          if (Math.hypot(cx-width*.5,cy-width*.475)<width*.333 && Math.hypot(cx-px,cy-py)<brush) this.cells.add(gx*24+gy);
        }
      }
    }
    reveal(moveFocus = false) {
      if (this.revealed) return;
      this.revealed = true; this.dragging = false;
      this.card.dataset.scratching = 'true'; this.card.dataset.revealed = 'true';
      this.card.setAttribute('aria-label','You have got 5% off');
      this.card.setAttribute('aria-disabled','true'); this.card.tabIndex = -1;
      this.querySelector('[data-status]').textContent = 'You have got 5% off. Select Claim to continue.';
      if (moveFocus) this.querySelector('[data-claim]').focus({ preventScroll: true });
      this.emit('revealed');
    }
    async clientRequest(resource, data) {
      const timeout = new AbortController();
      const timer = window.setTimeout(() => timeout.abort(), 15000);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/${resource}/?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify({ data }),
          signal: timeout.signal,
          credentials: 'omit'
        });
        if (!response.ok) throw new Error(response.status === 429 ? 'Please wait a moment and try again.' : 'We could not save that. Please try again.');
      } finally { window.clearTimeout(timer); }
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || this.emailCompleted) return;
      const input = this.form.querySelector('input[type="email"]');
      input.value = input.value.trim();
      if (!input.reportValidity()) return;
      if (this.preview) {
        this.emailCompleted = true;
        this.showStep('preferences');
        return;
      }
      const button = this.querySelector('[data-submit]');
      const error = this.querySelector('[data-email-error]');
      error.hidden = true;
      this.busy = true;
      button.disabled = true;
      button.textContent = 'Joining…';
      input.readOnly = true;
      this.emit('submitting');
      try {
        await this.clientRequest('subscriptions', {
          type: 'subscription',
          attributes: {
            custom_source: 'OcuLabs interactive signup 5% welcome',
            profile: { data: { type: 'profile', attributes: {
              email: input.value,
              subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              properties: { ocu_signup_source: 'interactive_signup_20', ocu_welcome_code: this.dataset.code, ocu_signup_page: window.location.pathname }
            } } }
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
        });
        this.email = input.value;
        this.emailCompleted = true;
        this.remember('subscribed');
        this.showStep('preferences');
      } catch (failure) {
        error.textContent = failure.name === 'AbortError' ? 'This is taking longer than expected. Please try again.' : failure.message;
        error.hidden = false;
        this.emit('signup_error');
      } finally {
        this.busy = false;
        button.disabled = false;
        button.textContent = 'Continue';
        input.readOnly = false;
      }
    }
    async chooseProduct(choice) {
      if (!this.emailCompleted || this.state !== 'preferences' || this.busy) return;
      const product = choice.dataset.productChoice;
      const label = choice.dataset.choiceLabel;
      const available = Boolean(choice.dataset.choiceUrl);
      if (!this.preview) {
        const error = this.querySelector('[data-choice-error]');
        const choices = this.querySelectorAll('[data-product-choice]');
        error.hidden = true;
        this.busy = true;
        for (const button of choices) button.disabled = true;
        try {
          await this.clientRequest('profiles', { type: 'profile', attributes: {
            email: this.email,
            properties: { ocu_product_interest: product, ocu_product_interest_label: label, ocu_signup_source: 'interactive_signup_20', ocu_welcome_code: this.dataset.code }
          } });
        } catch {
          error.textContent = 'Your email signup is saved. We could not save your product choice yet. Please select it again.';
          error.hidden = false;
          return;
        } finally {
          this.busy = false;
          for (const button of choices) button.disabled = false;
        }
      }

      let destination = new URL(choice.dataset.choiceUrl || 'https://ocupetlabs.com/collections/all', window.location.origin);
      if (!this.preview && this.dataset.code) {
        const discount = new URL(`/discount/${encodeURIComponent(this.dataset.code)}`, window.location.origin);
        discount.searchParams.set('redirect', destination.pathname + destination.search);
        destination = discount;
      }
      for (const link of this.querySelectorAll('[data-selected-shop]')) {
        link.href = destination.href;
        link.textContent = available ? `Shop ${label}` : 'Shop OcuLabs';
      }
      const message = this.querySelector('[data-selection-message]');
      message.hidden = available;
      message.textContent = available ? '' : `${label} is not available to shop yet. Explore our other products below.`;
      this.showStep('success');
      // Analytics event deliberately excludes the email address.
      this.emit('product_selected', { product, label });
    }
    emit(stage, details = {}) {
      this.dispatchEvent(new CustomEvent('oculabs:signup', { bubbles: true, detail: { ...details, stage, preview: this.preview } }));
    }
  }
  customElements.define('ocu-scratch-signup', OcuScratchSignup);
})();
